import React, { useEffect, useRef, useState } from 'react';
import { CloseButton, Modal, Popover } from '@mantine/core';
import { registerOverlayPortal } from '@puckeditor/core';
import IllustrationMedia from '../../../media/IllustrationMedia';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { puckArrayFieldPath } from '../../../utils/puckContentPath';
import { plainText } from '../../../utils/richTextHtml';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import { HotspotNumber, hotspotMarkerVariables } from './HotspotMarker';
import { hotspotMarkerStyle } from './hotspotPoints';
import { revealListItem } from '../../../puck/fields/listItemReveal';
import { useOwnerMediaQuery } from './useOwnerMediaQuery';
import WithBlockSelection, { noSelection } from '../../../puck/WithBlockSelection';
import classes from './HotspotImageBlock.module.css';

function HotspotContent({ id, point, index, puck, textStyles }) {
  return (
    <div className={classes.content}>
      <PuckInlineRichText id={id} puck={puck} html={point.title} contentPath={puckArrayFieldPath('points', index, 'title')} style={{ display: 'block', fontWeight: 600, ...textRoleCss(textStyles, 'title') }} placeholder="Titre du repère" bubbleStrategy="fixed" reducedToolbar />
      <PuckInlineRichText id={id} puck={puck} html={point.description} contentPath={puckArrayFieldPath('points', index, 'description')} style={{ display: 'block', ...textRoleCss(textStyles, 'description') }} placeholder="Explication" bubbleStrategy="fixed" />
      <IllustrationMedia media={point.media} blockId={id} path={puckArrayFieldPath('points', index, 'media')} isEditing={!!puck?.isEditing} alt={plainText(point.title)} />
    </div>
  );
}

function PointWindow({ children, isEditing, onActivate, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!isEditing || !ref.current) return undefined;
    return registerOverlayPortal(ref.current, { disableDragOnFocus: false });
  }, [isEditing]);
  return (
    <div ref={ref} className={classes.window} data-puck-overlay-portal={isEditing ? 'true' : undefined} onPointerDown={isEditing ? (event) => { event.stopPropagation(); onActivate(); } : undefined} onClick={(event) => event.stopPropagation()}>
      {onClose && <CloseButton className={classes.close} size="sm" aria-label="Fermer le repère" onClick={onClose} />}
      <div className={onClose ? classes.closable : undefined}>{children}</div>
    </div>
  );
}

function PointsLayer({
  selectBlock, id, src = '', points = [], hasImage = true, children, style, imageStyle,
  accent = '', markerSize = '2.75rem', panelBackground = '', panelWidth = '22rem', textStyles = {}, puck,
}) {
  const isEditing = !!puck?.isEditing;
  const rootRef = useRef(null);
  const mobile = useOwnerMediaQuery(rootRef, '(max-width: 48em)');
  const [opened, setOpened] = useState(null);
  useEffect(() => setOpened(null), [src]);
  useEffect(() => {
    const root = rootRef.current;
    const Observer = root?.ownerDocument.defaultView.IntersectionObserver;
    if (!opened || !Observer) return undefined;
    const observer = new Observer((entries) => {
      if (entries.every((entry) => !entry.isIntersecting)) setOpened(null);
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, [opened]);
  const openPoint = opened?.src === src ? points.find((point) => point.id === opened.id) : null;
  const panelStyle = { backgroundColor: panelBackground || 'var(--mantine-color-body)', color: 'var(--mantine-color-text)' };
  const content = (point, index) => <HotspotContent key={`${point.id}:${point.media?.src || ''}`} id={id} point={point} index={index} puck={puck} textStyles={textStyles} />;

  const imageAndPoints = () => (
    <>
      <div className={classes.image} style={imageStyle} data-hotspot-surface>
        {hasImage && children}
        {isEditing && !hasImage && <div className={classes.empty}>Choisissez l’image dans l’inspecteur : vous placerez ensuite les repères directement dessus.</div>}
        <div className={hasImage ? classes.markers : classes.fallback}>
          {points.map((point, index) => {
            const title = plainText(point.title) || `Repère ${index + 1}`;
            const trigger = <button
              type="button"
              data-hotspot-number={point.shape !== 'rectangle' || undefined}
              className={hasImage ? `${classes.marker} ${point.shape === 'rectangle' ? classes.rectangle : ''}` : classes.listButton}
              data-puck-overlay-portal={isEditing ? 'true' : undefined}
              style={hasImage ? hotspotMarkerStyle(point) : undefined}
              aria-label={plainText(point.title) ? `${index + 1}. ${title}` : title}
              aria-haspopup="dialog"
              aria-expanded={openPoint?.id === point.id}
              onClick={(event) => { event.stopPropagation(); selectBlock(); revealListItem(id, index); setOpened(openPoint?.id === point.id ? null : { id: point.id, src }); }}
              onPointerDown={isEditing ? (event) => event.stopPropagation() : undefined}
            >{hasImage ? <HotspotNumber point={point} index={index} /> : plainText(point.title) ? `${index + 1}. ${title}` : title}</button>;
            if (mobile) return <React.Fragment key={point.id}>{trigger}</React.Fragment>;
            return (
              <Popover key={point.id} opened={openPoint?.id === point.id} onChange={(next) => { if (!next) setOpened(null); }} position="bottom" withArrow withinPortal closeOnClickOutside={!isEditing} trapFocus={!isEditing} returnFocus shadow="md" width={`min(${panelWidth}, calc(100vw - 2rem))`}>
                <Popover.Target>{trigger}</Popover.Target>
                <Popover.Dropdown role="dialog" aria-label={title} style={{ ...panelStyle, maxHeight: 'min(32rem, 75dvh)', overflowY: 'auto' }}>
                  {openPoint?.id === point.id && <PointWindow isEditing={isEditing} onActivate={selectBlock} onClose={() => setOpened(null)}>{content(point, index)}</PointWindow>}
                </Popover.Dropdown>
              </Popover>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <div ref={rootRef} id={id} className={classes.root} style={{ ...style, ...hotspotMarkerVariables({ accent, markerSize }) }}>
      {imageAndPoints()}
      {mobile && <Modal portalProps={isEditing ? { target: rootRef.current?.ownerDocument.body } : undefined} opened={!!openPoint} onClose={() => setOpened(null)} title={openPoint ? plainText(openPoint.title) || `Repère ${points.indexOf(openPoint) + 1}` : ''} fullScreen closeButtonProps={{ 'aria-label': 'Fermer le repère' }} styles={{ content: panelStyle }}>
        {openPoint && <PointWindow isEditing={isEditing} onActivate={selectBlock}>{content(openPoint, points.indexOf(openPoint))}</PointWindow>}
      </Modal>}
    </div>
  );
}

export default function ImagePointsLayer(props) {
  const BlockSelection = props.puck?.isEditing ? WithBlockSelection : noSelection;
  return <BlockSelection blockId={props.id}>{(selectBlock) => <PointsLayer {...props} selectBlock={selectBlock} />}</BlockSelection>;
}
